import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingTips from '@/components/common/LoadingTips';
import { useHeldLoading } from '@/hooks/useHeldLoading';
import { LONG_PRESS_EVENT } from '@/hooks/useLongPress';

/**
 * ルート遷移中のローディング表示（#205 / docs/frontend-ui/loading-design.md §4.1）。
 *
 * react-router v7 + React 19 は `<Link>`/`navigate()` を startTransition でラップし、遷移中は
 * 「古い画面を保持」して Suspense フォールバックを出さない。そのため遷移先チャンクの DL 中は
 * 全画面ヒントが出ず、古い画面が居座る（＝ヒントが出るまで時間がかかる）。
 *
 * ローディング画面自体はメインバンドルに含まれる（遷移先チャンクを待たずに出せる）。ここでは
 * ナビゲーションを検知し:
 *  - クリック直後: 上部に細い進捗バー（即時フィードバック）。
 *  - 250ms 以上かかる遷移: **全画面ヒント（LoadingTips）をオーバーレイ表示**（遷移先を待たない）。
 *    オーバーレイは useHeldLoading で駆動し、一度出たら遷移コミット後も**最低2秒は表示を保持**する
 *    （遷移先データがキャッシュ済みで即描画されても、ヒントを最低2秒見せる）。
 * その後、遷移先ページのデータローダー（useHeldLoading）へ引き継ぐ。Tip・背景は共通なので、
 * オーバーレイ → データローダーは1画面のように連続する。
 *
 * 検知: 内部リンクの click（capture）と popstate（戻る/進む）。イベントカードの長押し
 * （アクションシート）直後に発火する click はナビゲーションではないため、useLongPress が
 * 発行する LONG_PRESS_EVENT を購読して無視する（押下時間の推測ではなく実測。#223）。
 */
export default function NavigationLoading() {
  const location = useLocation();
  const [navigating, setNavigating] = useState(false);
  // 誤検知時の脱出ハッチ: オーバーレイをタップ/クリックしたら以後この検知サイクルでは出さない
  const [dismissed, setDismissed] = useState(false);
  // 全画面ヒント（オーバーレイ）は useHeldLoading で駆動する。
  // 250ms 未満の速い遷移では出さず、一度出たら遷移コミット後も最低2秒表示を保持する
  // （遷移先データがキャッシュ済みで即描画される場合でも、ヒントを最低2秒見せる）。
  const overlay = useHeldLoading(navigating);
  const longPressRef = useRef(false);

  // 長押し発火（useLongPress からの通知）直後の click はナビゲーション扱いしない。
  // フラグは次の click で消費するが、環境によっては長押し後に click が発火しないため、
  // touchend から一定時間で自動破棄し、後続の正規クリックまで残らないようにする。
  useEffect(() => {
    let clearTimer: ReturnType<typeof setTimeout> | undefined;
    const onLongPress = () => {
      longPressRef.current = true;
    };
    const onTouchEnd = () => {
      if (!longPressRef.current) return;
      if (clearTimer) clearTimeout(clearTimer);
      clearTimer = setTimeout(() => {
        longPressRef.current = false;
      }, 700);
    };
    document.addEventListener(LONG_PRESS_EVENT, onLongPress);
    document.addEventListener('touchend', onTouchEnd, true);
    return () => {
      if (clearTimer) clearTimeout(clearTimer);
      document.removeEventListener(LONG_PRESS_EVENT, onLongPress);
      document.removeEventListener('touchend', onTouchEnd, true);
    };
  }, []);

  // 戻る/進む（popstate）もナビゲーションとして検知する。チャンク未取得のページへ
  // 戻る場合も click 経由と同じフィードバックを出す（#223）
  useEffect(() => {
    const onPopState = () => {
      setDismissed(false);
      setNavigating(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // 内部リンクのクリックでナビゲーション開始を検知
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (longPressRef.current) {
        longPressRef.current = false;
        return; // 長押し直後の click は無視
      }
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a');
      if (!anchor) return;
      // アンカーの内側に置かれた押せる要素（イベントカードの三点メニュー等）へのクリックは
      // preventDefault されて遷移しないが、capture フェーズでは子孫の preventDefault を
      // 観測できない。クリック対象とアンカーの間に該当要素があれば遷移扱いしない（#223）
      const interactive = target?.closest?.(
        'button, [role="menuitem"], [role="button"], input, select, textarea, label, summary, [data-action-button]'
      );
      if (interactive && anchor.contains(interactive)) return;
      const targetAttr = anchor.getAttribute('target');
      if (targetAttr && targetAttr !== '_self') return;
      if (anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:')
      ) {
        return;
      }
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return; // 外部リンク
      const current = window.location.pathname + window.location.search;
      if (url.pathname + url.search === current) return; // 同一 URL

      setDismissed(false);
      setNavigating(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // URL 変更（遷移コミット）で終了。effect 本体の同期 setState は避け timeout 経由。
  useEffect(() => {
    const id = setTimeout(() => setNavigating(false), 0);
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);

  // 安全策: 一定時間で自動的に終了（遷移が起きなかった場合など）。
  // 誤検知したときの唯一の回復手段なので、体感的に許容できる長さに抑える（#223）
  useEffect(() => {
    if (!navigating) return;
    const id = setTimeout(() => setNavigating(false), 5000);
    return () => clearTimeout(id);
  }, [navigating]);

  return (
    <>
      {navigating && (
        <div
          className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-0.5 overflow-hidden"
          role="status"
          aria-label="読み込み中"
        >
          <div
            className="h-full w-full origin-left bg-primary-500"
            style={{ animation: 'nav-progress 10s cubic-bezier(0.1, 0.7, 0.3, 1) forwards' }}
          />
        </div>
      )}
      {overlay && !dismissed && (
        <div
          className="fixed inset-0 z-[90] bg-text-50"
          // 誤検知時の脱出ハッチ: タップ/クリックで即時に閉じる。正規の遷移中に閉じても
          // 古い画面が見えるだけで、遷移自体は継続してコミットされるため害はない
          onPointerDown={() => setDismissed(true)}
        >
          <LoadingTips delayMs={0} className="bg-text-50" />
        </div>
      )}
    </>
  );
}
